import { el, clear } from './dom.js';
import { icon } from './icons.js';
import { splitHint, shortLabel, iconFor, keyFor } from './actions.js';
import { t } from '../../shared/i18n.js';

const NEED_ICON = { hunger: 'bowl', energy: 'bolt', fun: 'smile', social: 'family', hygiene: 'drop', bladder: 'toilet' };
const NEED_NAME = { hunger: 'Hunger', energy: 'Energy', fun: 'Fun', social: 'Social', hygiene: 'Hygiene', bladder: 'Bladder' };
const needName = (n) => t(NEED_NAME[n] || n);
const TOAST_MS = { info: 3500, success: 4000, money: 5000, warning: 6000, danger: 8000 };
const TOAST_ICON = { info: 'info', success: 'check', money: 'coin', warning: 'warning', danger: 'danger' };
const RING = 2 * Math.PI * 9; // the needs rings: radius 9

// On-screen information: where you are, the time and sky, money and needs, the actions that
// apply right now, notices, and the loading screen.
export class Hud {
  constructor(root) {
    this.place = el('div.place', null, 'Bamenda');
    this.where = el('div.where');
    this.clock = el('span.clock');
    this.skyIcon = el('span.sky-ic');
    this.sky = el('span.sky');
    this.moneyNode = el('div.money', { hidden: true }, icon('coin', 18), (this.money = el('span.amount')));
    this.needsNode = el('div.needs', { hidden: true });
    this.warn = el('div.warn', { hidden: true });
    this.routeIcon = el('span.route-ic');
    this.routeText = el('span.route-text');
    this.routeNode = el('div', { id: 'gps', hidden: true }, this.routeIcon, this.routeText);
    this.needRings = {};
    // the dock: the one to three things you can do here, and a "more" sheet with the rest
    this.dockActions = el('div.dock-actions');
    this.moreButton = el('button.btn.btn-secondary.btn-lg.more', { type: 'button', 'aria-label': t('All controls'), 'aria-expanded': 'false', onclick: () => this.toggleMore() }, icon('menu', 22));
    this.dock = el('div', { id: 'dock' }, this.dockActions, this.moreButton);
    this.moreList = el('div.more-list');
    this.more = el('div.more-sheet.card', { id: 'moresheet', hidden: true, role: 'dialog', 'aria-label': t('All controls') }, el('div.more-head', null, el('h3', null, t('Controls')), el('button.btn.btn-ghost.btn-sm', { type: 'button', 'aria-label': t('Close'), onclick: () => this.toggleMore(false) }, icon('close', 18))), this.moreList);
    this.hint = el('div.sr-only', { id: 'hint' }); // the full list, for screen readers and tests
    this.perfNode = el('div', { id: 'perf', hidden: true });
    this.toasts = el('div', { id: 'toasts', 'aria-live': 'polite' });
    this.toastNode = el('div.sr-only', { id: 'toast', role: 'status' }); // mirrors the latest notice
    this.queue = [];
    this.showing = [];
    this.passedNode = el('div', { id: 'passed', hidden: true, role: 'status' });
    this.loadText = el('p', null, 'Starting');
    this.loadBar = el('i');
    this.loadingNode = el('div', { id: 'loading' }, el('h1', null, 'Abakwa Mythlands', el('span')), this.loadText, el('div.bar', null, this.loadBar));
    root.append(
      el('div.hud-card', { id: 'board' }, this.place, this.where, el('div.clockline', null, icon('clock', 14), this.clock, el('span.dot', null, '·'), this.skyIcon, this.sky)),
      el('div.hud-card', { id: 'status' }, this.moneyNode, this.needsNode, this.warn),
      this.routeNode,
      this.dock,
      this.more,
      this.hint,
      this.perfNode,
      this.toasts,
      this.toastNode,
      this.passedNode,
      el('div', { id: 'credit' }, 'Map data © OpenStreetMap contributors'),
      this.loadingNode
    );
  }

  loading(fraction, text) {
    this.loadText.textContent = text;
    this.loadBar.style.width = Math.round(fraction * 100) + '%';
  }

  loaded() {
    this.loadingNode.hidden = true;
  }

  fail(message) {
    this.loadText.className = 'error';
    this.loadText.textContent = message;
  }

  setPlace(name, where) {
    if (this.place.textContent !== name) this.place.textContent = name;
    if (this.where.textContent !== where) this.where.textContent = where;
  }

  // { time: '11:00', day: 'Thursday 8 October', weather: '19°C, clear sky', money: 8000, tired: false }
  setStatus(s) {
    if (this.clock.textContent !== s.time) this.clock.textContent = s.time;
    const sky = s.weather ? `${s.day} · ${s.weather}` : s.day || '';
    if (this.sky.textContent !== sky) {
      this.sky.textContent = sky;
      const w = (s.weather || '').toLowerCase();
      const hour = Number((s.time || '12').split(':')[0]);
      const name = /rain|shower|storm|drizzle/.test(w) ? 'rain' : /cloud|overcast|mist|fog|haze/.test(w) ? 'cloud' : hour < 6 || hour >= 19 ? 'moon' : 'sun';
      if (this.skyName !== name) {
        this.skyName = name;
        clear(this.skyIcon);
        this.skyIcon.append(icon(name, 14));
      }
    }
    if (s.money !== null && s.money !== undefined) {
      const text = s.money.toLocaleString('en-US') + ' FCFA';
      this.moneyNode.hidden = false;
      if (this.money.textContent !== text) this.money.textContent = text;
    }
    const warn = s.tired ? t('You dey tire: chop or sleep') : '';
    if (this.warn.textContent !== warn) {
      this.warn.textContent = warn;
      this.warn.hidden = !warn;
    }
  }

  // The way to the goal: text and the next turn ({ side: 'left'|'right'|null }), or '' to hide.
  setRoute(text, turn) {
    if (!text) {
      this.routeNode.hidden = true;
      return;
    }
    this.routeNode.hidden = false;
    if (this.routeText.textContent !== text) this.routeText.textContent = text;
    const name = turn && turn.side === 'left' ? 'turnleft' : turn && turn.side === 'right' ? 'turnright' : 'straight';
    if (this.routeIconName !== name) {
      this.routeIconName = name;
      clear(this.routeIcon);
      this.routeIcon.append(icon(name, 22));
    }
  }

  // kept for callers that only know the old shape
  setClock(time, sky) {
    this.setStatus({ time, day: sky });
  }

  // needs: { hunger, energy, fun, social, hygiene, bladder } 0-100; a ring each, red when low
  setNeeds(needs) {
    this.needsNode.hidden = false;
    for (const [name, v] of Object.entries(needs)) {
      let ring = this.needRings[name];
      if (!ring) {
        const ns = 'http://www.w3.org/2000/svg';
        const circle = (cls) => {
          const c = document.createElementNS(ns, 'circle');
          c.setAttribute('class', cls);
          c.setAttribute('cx', '12');
          c.setAttribute('cy', '12');
          c.setAttribute('r', '9');
          return c;
        };
        const fill = circle('fill');
        fill.setAttribute('stroke-dasharray', RING.toFixed(2));
        const svg = document.createElementNS(ns, 'svg');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('class', 'ring');
        svg.append(circle('track'), fill);
        const span = el('span', { title: needName(name), role: 'img' }, svg, icon(NEED_ICON[name] || 'info', 12));
        ring = { fill, span, last: -1 };
        this.needRings[name] = ring;
        this.needsNode.append(span);
      }
      const r = Math.round(v);
      if (r === ring.last) continue;
      ring.last = r;
      ring.fill.setAttribute('stroke-dashoffset', (RING * (1 - r / 100)).toFixed(2));
      ring.span.className = r < 25 ? 'low' : r < 50 ? 'mid' : '';
      ring.span.setAttribute('aria-label', `${needName(name)} ${r}%`);
    }
  }

  // parts: alternating key name and what it does, e.g. ['W A S D', 'walk', 'Shift', 'run']
  setHint(parts) {
    const key = parts.join('|');
    if (this.hintKey === key) return;
    this.hintKey = key;
    this.hintParts = parts;
    if (this.onHint) this.onHint(parts); // phone buttons are made from the same hints
    clear(this.hint);
    for (let i = 0; i < parts.length; i += 2) this.hint.append(el('span', null, el('b', null, parts[i]), ' ' + shortLabel(parts[i + 1])));
    const { primary, rest } = splitHint(parts);
    clear(this.dockActions);
    for (const [name, does] of primary) {
      const k = keyFor(name);
      this.dockActions.append(el('button.btn.btn-primary.btn-lg.action', { type: 'button', 'data-key': name, onclick: () => k && this.onAction && this.onAction(k) }, icon(iconFor(does), 22), el('span.label', null, shortLabel(does)), el('kbd', null, name)));
    }
    this.dock.classList.toggle('empty', primary.length === 0);
    this.restParts = rest;
    if (!this.more.hidden) this.fillMore();
  }

  fillMore() {
    clear(this.moreList);
    for (const [name, does] of this.restParts || []) {
      const k = keyFor(name);
      this.moreList.append(el('button.more-item', { type: 'button', disabled: !k, onclick: () => { if (k && this.onAction) this.onAction(k); this.toggleMore(false); } }, el('kbd', null, name), el('span', null, shortLabel(does))));
    }
  }

  toggleMore(open = this.more.hidden) {
    this.more.hidden = !open;
    this.moreButton.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) this.fillMore();
  }

  // The performance overlay (?perf=1): numbers the budget document asks for.
  setPerf(p) {
    this.perfNode.hidden = false;
    const text = `${p.fps} fps · p95 ${p.p95} ms · ${p.calls} draw calls · ${(p.triangles / 1000).toFixed(0)}k tris · ${p.geometries} geom · ${p.textures} tex · ${p.longTasks} stalls (max ${p.longest} ms)${p.gpu !== null ? ` · gpu ${p.gpu} ms` : ''}${p.physics !== null ? ` · physics ${p.physics < 0 ? 'loading' : p.physics + ' ms lag'}` : ''}${p.wet ? ` · wet ${p.wet}` : ''} · ${p.tier}/${p.gfx}/${p.stream}`;
    if (this.perfNode.textContent !== text) this.perfNode.textContent = text;
  }

  // The big card when a mission is done: the title, the reward, then what comes next; it fades
  // on its own. onPassed() is called for the chime.
  passed(title, reward, next) {
    clear(this.passedNode);
    this.passedNode.hidden = false;
    this.passedNode.className = '';
    this.passedNode.append(
      el('div.passed-kicker', null, t('Mission passed')),
      el('div.passed-title', null, title),
      reward ? el('div.passed-reward', null, icon('coin', 22), `+${reward.toLocaleString('en-US')} FCFA`) : null,
      next ? el('div.passed-next', null, el('span.muted', null, t('Next')), ' ', next) : null
    );
    if (this.onPassed) this.onPassed();
    clearTimeout(this.passedTimer);
    requestAnimationFrame(() => this.passedNode.classList.add('in'));
    this.passedTimer = setTimeout(() => {
      this.passedNode.classList.add('out');
      setTimeout(() => {
        this.passedNode.hidden = true;
      }, 600);
    }, 4200);
  }

  // toast(text, ms) or toast(text, { kind: 'info'|'success'|'money'|'warning'|'danger', ms, sticky })
  // At most three show at once; the rest wait their turn; a repeat within two seconds is counted
  // on the one already showing instead of shown again.
  toast(message, opts) {
    const o = typeof opts === 'number' ? { ms: opts } : opts || {};
    const kind = o.kind || 'info';
    const now = Date.now();
    const same = this.showing.find((x) => x.text === message && now - x.at < 2000);
    if (same) {
      same.count++;
      same.countNode.textContent = `×${same.count}`;
      same.countNode.hidden = false;
      return;
    }
    this.toastNode.textContent = message;
    this.queue.push({ text: message, kind, ms: o.sticky ? 0 : o.ms || TOAST_MS[kind] || 3500, at: now, count: 1 });
    this.drain();
  }

  drain() {
    while (this.showing.length < 3 && this.queue.length) {
      const x = this.queue.shift();
      x.at = Date.now();
      x.countNode = el('span.pill.pill-accent', { hidden: true });
      x.node = el(`div.toast.toast-${x.kind}`, { role: 'status' }, el('span.toast-ic', null, icon(TOAST_ICON[x.kind] || 'info', 18)), el('span.text', null, x.text), x.countNode, x.ms ? null : el('button.btn.btn-ghost.btn-sm.toast-x', { type: 'button', 'aria-label': t('Dismiss'), onclick: () => this.dismiss(x) }, icon('close', 16)));
      if (!x.ms) x.node.addEventListener('click', () => this.dismiss(x));
      this.toasts.append(x.node);
      this.showing.push(x);
      requestAnimationFrame(() => x.node.classList.add('in'));
      if (x.ms) x.timer = setTimeout(() => this.dismiss(x), x.ms);
    }
  }

  dismiss(x) {
    const k = this.showing.indexOf(x);
    if (k < 0) return;
    this.showing.splice(k, 1);
    clearTimeout(x.timer);
    x.node.classList.add('leaving');
    let done = false;
    const gone = () => {
      if (done) return;
      done = true;
      x.node.remove();
      this.drain();
    };
    x.node.addEventListener('transitionend', gone, { once: true });
    setTimeout(gone, 320);
  }
}
